import React, { useEffect, useState } from "react";
import backend from "../../../backend";
import useIsMobile from "../../../hooks/useIsMobile";

// Panel Configuración → General — rediseño 28-ago:
// tarjetas compactas de conexiones en grilla 2 columnas (1 en móvil),
// formularios colapsados por defecto y blindaje anti-autofill de Chrome.
// Endpoints intactos: GET sii/estado · POST sii/credenciales ·
// GET credencialExterna · POST credencialExterna {servicio, usuario, clave}.

const SERVICIOS = [
  {
    servicio: "previred",
    icono: "🏛️",
    titulo: "Previred",
    nota: "La usa el robot mensual que descarga planillas pagadas y certificados (solo lectura).",
  },
  {
    servicio: "enternet",
    icono: "📨",
    titulo: "Enternet",
    nota: "La usará el robot de cosecha de documentos hasta el corte del contrato (solo lectura).",
  },
  {
    servicio: "digitalocean",
    icono: "☁️",
    titulo: "DigitalOcean",
    nota: "Token de API de solo lectura para ver los VPS y el consumo, y consolidar costos. El usuario queda como 'api-token'; pegue el token en el campo de clave.",
  },
  {
    servicio: "recepcion_gmail",
    icono: "📬",
    titulo: "Correo recepcion@ (vigía de OC)",
    nota: "App Password de recepcion@binimeliz.com — se crea en myaccount.google.com → Seguridad → Contraseñas de aplicaciones.",
  },
  {
    servicio: "contabilidad_gmail",
    icono: "🧮",
    titulo: "Correo contabilidad@ (canal con el contador externo)",
    nota: "App Password de contabilidad@binimeliz.com — con la sesión de esa cuenta: myaccount.google.com → Seguridad → Verificación en dos pasos → Contraseñas de aplicaciones. El vigía lee lo que llega y lo deja como consulta al agente contador.",
  },
];

const GeneralPanel = () => {
  const isMobile = useIsMobile();

  const [siiEstado, setSiiEstado] = useState(null);
  const [siiRut,    setSiiRut]    = useState("");
  const [siiClave,  setSiiClave]  = useState("");
  const [siiSaving, setSiiSaving] = useState(false);
  const [siiMsg,    setSiiMsg]    = useState(null);

  // Credenciales externas (27-ago): mismo blindaje que el SII — se cifran en el
  // server, jamás vuelven al frontend; las usan los robots de extracción.
  const [prevList,  setPrevList]  = useState([]);
  const [credForm,  setCredForm]  = useState({ previred: { usuario: "", clave: "" }, enternet: { usuario: "", clave: "" }, digitalocean: { usuario: "api-token", clave: "" }, contabilidad_gmail: { usuario: "contabilidad@binimeliz.com", clave: "" }, recepcion_gmail: { usuario: "recepcion@binimeliz.com", clave: "" } });
  const [prevSaving, setPrevSaving] = useState(null); // servicio guardándose
  const [prevMsg,   setPrevMsg]   = useState(null);   // { servicio, ok, text }

  // Qué formularios están abiertos (colapsados por defecto)
  const [abierto, setAbierto] = useState({}); // { [servicio|"sii"]: bool }
  const toggle = (key) => setAbierto(p => ({ ...p, [key]: !p[key] }));

  const cargarCreds = () => backend.get("credencialExterna").then(r => setPrevList(r.data || [])).catch(() => {});

  useEffect(() => {
    backend.get("sii/estado")
      .then(r => setSiiEstado(r.data))
      .catch(() => setSiiEstado({ conectado: false }));
    cargarCreds();
  }, []);

  const guardarCred = async (servicio) => {
    const f = credForm[servicio];
    if (!f.usuario || !f.clave) { setPrevMsg({ servicio, ok: false, text: "Usuario y clave requeridos" }); return; }
    setPrevSaving(servicio); setPrevMsg(null);
    try {
      await backend.post("credencialExterna", { servicio, usuario: f.usuario, clave: f.clave });
      setCredForm(p => ({ ...p, [servicio]: { usuario: "", clave: "" } }));
      setPrevMsg({ servicio, ok: true, text: "✅ Credencial guardada cifrada — el robot queda habilitado" });
      setAbierto(p => ({ ...p, [servicio]: false }));
      cargarCreds();
    } catch (e) {
      setPrevMsg({ servicio, ok: false, text: "Error: " + (e?.response?.data?.error || e.message) });
    }
    setPrevSaving(null);
  };

  const revocarCred = async (servicio, titulo) => {
    if (!window.confirm(`¿Revocar la credencial de ${titulo}? El robot dejará de acceder al servicio.`)) return;
    setPrevSaving(servicio); setPrevMsg(null);
    try {
      await backend.delete(`credencialExterna/${servicio}`);
      setPrevList(prev => prev.filter(c => c.servicio !== servicio));
      setPrevMsg({ servicio, ok: true, text: "Credencial revocada" });
      setAbierto(p => ({ ...p, [servicio]: false }));
    } catch (e) {
      setPrevMsg({ servicio, ok: false, text: "Error: " + (e?.response?.data?.error || e.message) });
    }
    setPrevSaving(null);
  };

  const guardarSii = async () => {
    if (!siiRut || !siiClave) {
      setSiiMsg({ ok: false, text: "RUT y clave requeridos" });
      return;
    }
    setSiiSaving(true);
    setSiiMsg(null);
    try {
      const r = await backend.post("sii/credenciales", { rut: siiRut, clave: siiClave });
      setSiiClave("");
      setSiiEstado({ conectado: true, rut: r.data.rut });
      setSiiMsg({
        ok: true,
        text: r.data.verificado
          ? "✅ Credenciales guardadas y verificadas con el SII"
          : "⚠ Credenciales guardadas pero no se pudo verificar la conexión con el SII",
      });
      setAbierto(p => ({ ...p, sii: false }));
    } catch (e) {
      setSiiMsg({ ok: false, text: "Error: " + (e?.response?.data?.error || e.message) });
    }
    setSiiSaving(false);
  };

  // ——— estilos compartidos ———
  const inputStyle = {
    background: "#161616", border: "1px solid #333", borderRadius: 6,
    padding: isMobile ? "10px 12px" : "8px 10px",
    fontSize: 16, color: "#ddd",
    fontFamily: "Arial,sans-serif", width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4,
  };
  const btnPrimario = (saving) => ({
    background: saving ? "#333" : "#e30613", color: "#fff", border: "none",
    borderRadius: 6, padding: isMobile ? "11px 12px" : "9px 12px", fontSize: 13, fontWeight: 700,
    cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1,
  });
  const btnSecundario = {
    background: "transparent", color: "#999", border: "1px solid #3a3a3a",
    borderRadius: 6, padding: isMobile ? "11px 12px" : "9px 12px", fontSize: 13, fontWeight: 700,
    cursor: "pointer",
  };
  const cardStyle = {
    background: "#232323", border: "1px solid #2e2e2e",
    borderRadius: 12, padding: "16px 18px",
    display: "flex", flexDirection: "column", gap: 12,
  };
  const chipStyle = (ok) => ({
    display: "inline-flex", alignItems: "center", gap: 6,
    fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
    background: ok ? "#0d2a15" : "#2a2a2a",
    color: ok ? "#2ecc71" : "#999",
    border: `1px solid ${ok ? "#0d4020" : "#3a3a3a"}`,
    whiteSpace: "nowrap",
  });
  const msgStyle = (ok) => ({
    fontSize: 12, padding: "8px 10px", borderRadius: 6,
    background: ok ? "#0d2a15" : "#2d1515",
    color: ok ? "#2ecc71" : "#e74c3c",
    border: `1px solid ${ok ? "#0d4020" : "#4a2020"}`,
  });

  const Punto = ({ ok }) => (
    <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: ok ? "#2ecc71" : "#777" }} />
  );

  const CabeceraCard = ({ icono, titulo, chip }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: "#1a1a1a", border: "1px solid #333",
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
      }}>{icono}</div>
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#eee", lineHeight: 1.3 }}>{titulo}</div>
        <div>{chip}</div>
      </div>
    </div>
  );

  const siiAbierto = !!abierto.sii;
  const credGuardada = (servicio) => prevList.find(c => c.servicio === servicio);

  return (
    <div>
      <div style={{
        fontSize: 12, fontWeight: 700, color: "#999",
        textTransform: "uppercase", letterSpacing: ".07em",
        margin: "0 0 12px",
      }}>
        Conexiones
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
        gap: 14, maxWidth: 1000, alignItems: "start",
      }}>

        {/* ——— Tarjeta SII ——— */}
        <div style={cardStyle}>
          <CabeceraCard
            icono="🧾"
            titulo="SII — Clave Tributaria"
            chip={
              <span style={chipStyle(!!siiEstado?.conectado)}>
                <Punto ok={!!siiEstado?.conectado} />
                {siiEstado?.conectado ? `Conectada — ${siiEstado.rut}` : "Sin configurar"}
              </span>
            }
          />
          {siiEstado?.ultimaSync && (
            <div style={{ fontSize: 11, color: "#999" }}>
              Última sync: {new Date(siiEstado.ultimaSync).toLocaleString("es-CL")}
            </div>
          )}

          {siiMsg && <div style={msgStyle(siiMsg.ok)}>{siiMsg.text}</div>}

          {!siiAbierto ? (
            <button type="button" onClick={() => toggle("sii")} style={{ ...btnSecundario, alignSelf: "flex-start" }}>
              {siiEstado?.conectado ? "Reemplazar clave" : "Configurar"}
            </button>
          ) : (
            <form
              autoComplete="off"
              onSubmit={e => { e.preventDefault(); guardarSii(); }}
              style={{ display: "flex", flexDirection: "column", gap: 10 }}
            >
              <div>
                <div style={labelStyle}>RUT empresa (sin puntos, con guión)</div>
                <input
                  name="rut-empresa-sii"
                  autoComplete="off"
                  value={siiRut}
                  onChange={e => setSiiRut(e.target.value)}
                  placeholder="96911420-8"
                  style={inputStyle}
                />
              </div>
              <div>
                <div style={labelStyle}>Clave Tributaria SII</div>
                <input
                  type="password"
                  name="clave-tributaria-sii"
                  autoComplete="new-password"
                  value={siiClave}
                  onChange={e => setSiiClave(e.target.value)}
                  placeholder={siiEstado?.conectado ? "••••••• (dejar vacío para no cambiar)" : "Clave tributaria"}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={siiSaving} style={{ ...btnPrimario(siiSaving), flex: 1 }}>
                  {siiSaving ? "Verificando…" : "Guardar y verificar"}
                </button>
                <button type="button" onClick={() => toggle("sii")} style={btnSecundario}>
                  Cancelar
                </button>
              </div>
            </form>
          )}

          <div style={{ fontSize: 11, color: "#999", lineHeight: 1.6 }}>
            La clave se cifra AES-256 en el servidor y nunca vuelve a mostrarse.
            La verificación de conexión usa el certificado digital (SimpleAPI).
          </div>
        </div>

        {/* ——— Tarjetas de credenciales externas ——— */}
        {SERVICIOS.map(({ servicio, icono, titulo, nota }) => {
          const guardada = credGuardada(servicio);
          const abiertoEste = !!abierto[servicio];
          const esVigia = servicio === "recepcion_gmail";
          return (
            <div key={servicio} style={cardStyle}>
              <CabeceraCard
                icono={icono}
                titulo={titulo}
                chip={
                  <span style={chipStyle(!!guardada)}>
                    <Punto ok={!!guardada} />
                    {guardada ? "Guardada" : "Sin configurar"}
                  </span>
                }
              />
              {guardada && (
                <div style={{ fontSize: 11, color: "#999" }}>
                  Usuario {guardada.usuario} · {guardada.ultimoUso ? `último uso ${new Date(guardada.ultimoUso).toLocaleDateString("es-CL")}` : "aún sin uso"}
                </div>
              )}

              {esVigia && (
                <div style={{ fontSize: 11, color: guardada ? "#2ecc71" : "#c9a227", lineHeight: 1.5 }}>
                  {guardada
                    ? "El vigía revisa el buzón cada 5 minutos y alerta OC no registradas"
                    : "Vigía dormido — falta la App Password (myaccount.google.com → Seguridad → Contraseñas de aplicaciones)"}
                </div>
              )}

              {prevMsg?.servicio === servicio && <div style={msgStyle(prevMsg.ok)}>{prevMsg.text}</div>}

              {!abiertoEste ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={() => toggle(servicio)} style={{ ...btnSecundario, alignSelf: "flex-start" }}>
                    {guardada ? "Reemplazar clave" : "Configurar"}
                  </button>
                  {guardada && <button type="button" disabled={prevSaving === servicio} onClick={() => revocarCred(servicio, titulo)}
                    style={{ ...btnSecundario, color: "#e74c3c", borderColor: "#633" }}>Revocar</button>}
                </div>
              ) : (
                <form
                  autoComplete="off"
                  onSubmit={e => { e.preventDefault(); guardarCred(servicio); }}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  <div>
                    <div style={labelStyle}>Usuario / RUT de acceso</div>
                    <input
                      name={`usuario-${servicio}-ext`}
                      autoComplete="off"
                      value={credForm[servicio].usuario}
                      onChange={e => setCredForm(p => ({ ...p, [servicio]: { ...p[servicio], usuario: e.target.value } }))}
                      placeholder={`Usuario / RUT de acceso a ${titulo}`}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <div style={labelStyle}>Clave</div>
                    <input
                      type="password"
                      name={`clave-${servicio}-ext`}
                      autoComplete="new-password"
                      value={credForm[servicio].clave}
                      onChange={e => setCredForm(p => ({ ...p, [servicio]: { ...p[servicio], clave: e.target.value } }))}
                      placeholder={`Clave ${titulo}`}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="submit" disabled={prevSaving === servicio} style={{ ...btnPrimario(prevSaving === servicio), flex: 1 }}>
                      {prevSaving === servicio ? "Guardando…" : "Guardar cifrada"}
                    </button>
                    <button type="button" onClick={() => toggle(servicio)} style={btnSecundario}>
                      Cancelar
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "#999", lineHeight: 1.6 }}>
                    Se cifra AES-256 en el servidor y nunca vuelve a mostrarse. {nota}
                  </div>
                </form>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GeneralPanel;
